// @flow

import React, { PureComponent } from 'react';
import { Layer } from 'react-mapbox-gl';
import { connect } from 'react-redux';
import {
  selectRowLevelChangepoint,
} from '../modules/map';

type Props = {
  rowLevelChangepoint: number,
};

const layout = {
  rowLabel: {
    'text-size': [
      'interpolate', ['exponential', 1], ['zoom'],
      4, 4,
      8, 10,
    ],
    'text-padding': 2,
    'text-ignore-placement': false,
    'text-font': ['Roboto Regular'],
    'text-field': '{name}',
    'symbol-avoid-edges': true,
    'text-line-height': 1.2,
  },
  sectionLabel: {
    'text-line-height': 4,
    'text-size': [
      'interpolate', ['exponential', 1], ['zoom'],
      3, 8,
      7, 22,
    ],
    'symbol-avoid-edges': true,
    'symbol-spacing': 500,
    'text-font': ['Roboto Medium'],
    'text-padding': 6,
    'text-rotation-alignment': 'viewport',
    'text-pitch-alignment': 'viewport',
    'text-field': '{name}',
    'text-rotate': 0,
    'text-max-width': 7,
  },
  poiLabel: {
    'text-line-height': 2,
    'text-size': [
      'interpolate', ['exponential', 1.5], ['zoom'],
      4, 12,
      8, 18,
    ],
    'symbol-avoid-edges': true,
    'text-font': ['Roboto Regular'],
    'text-padding': 6,
    'text-rotation-alignment': 'map',
    'text-pitch-alignment': 'map',
    'text-field': '{name}',
    'text-rotate': ['case', ['has', 'position'], ['get', 'position'], 0],
    'text-max-width': 20,
  },
  stageLabel: {
    'text-size': [
      'interpolate', ['exponential', 1], ['zoom'],
      4, 10,
      8, 20,
    ],
    'symbol-avoid-edges': true,
    'text-font': ['Roboto Medium'],
    'text-field': 'STAGE',
    'text-padding': 6,
    'text-max-width': 4,
    'text-line-height': 4,
  },
  footballLabel: {
    'text-field': '{text}',
    'text-font': ['Clarendon BT Bold'],
    'text-size': [
      'interpolate', ['exponential', 1.5], ['zoom'],
      2, 0,
      5, 15,
      8, 80,
    ],
    'text-rotate': ['case', ['has', 'position'], ['get', 'position'], 0],
    'text-pitch-alignment': 'map',
    'text-rotation-alignment': 'map',
    'text-allow-overlap': true,
  },
  footballTeamNames: {
    'text-size': [
      'interpolate', ['exponential', 1.25], ['zoom'],
      2, 4,
      8, 36,
    ],
    'text-font': ['Roboto Medium'],
    'text-offset': [0, 0],
    'text-rotation-alignment': 'map',
    'text-pitch-alignment': 'map',
    'text-field': '{text}',
    'text-rotate': 0,
    'text-max-width': 40,
  },
};

const filter = {
  rowLabel: ['all',
    ['==', ['geometry-type'], 'Point'],
    ['==', ['get', 'type'], 'row-label'],
  ],
  sectionLabel: ['all',
    ['==', ['geometry-type'], 'Point'],
    ['==', ['get', 'type'], 'section-label'],
  ],
  poiLabel: ['all',
    ['==', ['geometry-type'], 'Point'],
    ['==', ['get', 'type'], 'poi'],
  ],
  stageLabel: ['all',
    ['==', ['geometry-type'], 'Point'],
    ['==', ['get', 'type'], 'stage'],
  ],
  footballLabel: ['all',
    ['==', ['geometry-type'], 'Point'],
    ['==', ['get', 'type'], 'marker'],
    ['match', ['get', 'text'], ['0', '1', '2', '3', '4', '5'], true, false],
  ],
  footballTeamNames: ['all',
    ['==', ['geometry-type'], 'Point'],
    ['==', ['get', 'type'], 'marker'],
    ['match', ['get', 'text'], ['0', '1', '2', '3', '4', '5'], false, true],
  ],
};

const paint = {
  rowLabel: {
    'text-color': '#000',
    'text-halo-color': '#fff',
    'text-halo-width': 0.25,
  },
  sectionLabel: {
    'text-color': 'rgb(66,66,66)',
    'text-halo-color': 'rgba(255,255,255,.8)',
    'text-halo-width': [
      'interpolate', ['exponential', 1], ['zoom'],
      3, 1,
      7, 2,
    ],
  },
  poiLabel: {
    'text-color': 'rgb(66,66,66)',
    'text-halo-color': 'rgba(255,255,255,.8)',
    'text-halo-width': [
      'interpolate', ['exponential', 1], ['zoom'],
      5, 1,
      8, 2,
    ],
  },
  stageLabel: {
    'text-color': 'rgb(175,175,175)',
  },
  footballLabel: {
    'text-color': '#fff',
  },
  footballTeamNames: {
    'text-color': '#fff',
  },
};

class LabelStyle extends PureComponent<Props> {
  render() {
    return (
      <div>
        <Layer
          id="row-label"
          type="symbol"
          sourceId="venue"
          sourceLayer="data"
          layout={layout.rowLabel}
          filter={filter.rowLabel}
          paint={paint.rowLabel}
          minZoom={this.props.rowLevelChangepoint}
        />
        <Layer
          id="section-label"
          type="symbol"
          sourceId="venue"
          sourceLayer="data"
          layout={layout.sectionLabel}
          filter={filter.sectionLabel}
          paint={paint.sectionLabel}
        />
        <Layer
          id="poi-label"
          type="symbol"
          sourceId="venue"
          sourceLayer="data"
          layout={layout.poiLabel}
          filter={filter.poiLabel}
          paint={paint.poiLabel}
        />
        <Layer
          id="stage-label"
          type="symbol"
          sourceId="venue"
          sourceLayer="data"
          layout={layout.stageLabel}
          filter={filter.stageLabel}
          paint={paint.stageLabel}
        />
        <Layer
          id="football-label"
          type="symbol"
          sourceId="venue"
          sourceLayer="data"
          layout={layout.footballLabel}
          filter={filter.footballLabel}
          paint={paint.footballLabel}
        />
        <Layer
          id="football-team-names"
          type="symbol"
          sourceId="venue"
          sourceLayer="data"
          layout={layout.footballTeamNames}
          filter={filter.footballTeamNames}
          paint={paint.footballTeamNames}
        />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  rowLevelChangepoint: selectRowLevelChangepoint(state),
});

export default connect(mapStateToProps, null)(LabelStyle);



// WEBPACK FOOTER //
// ./src/styles/labels.js